import ExcelJS from 'exceljs';

type Cell = string | number;

interface SheetSpec {
  name: string;
  headers: string[];
  data: Cell[][];
}

/** Construye un .xlsx con exceljs (una hoja por spec: encabezados + filas de ejemplo) y lo descarga. */
async function downloadWorkbook(fileName: string, sheets: SheetSpec[]) {
  const wb = new ExcelJS.Workbook();
  for (const sheet of sheets) {
    const ws = wb.addWorksheet(sheet.name);
    ws.addRow(sheet.headers);
    for (const row of sheet.data) ws.addRow(row);
  }
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const RANCHOS_SHEET: SheetSpec = {
  name: 'Ranchos',
  headers: ['nombre', 'ubicacion', 'hectareas'],
  data: [
    ['Rancho Ejemplo 1', 'Ébano, SLP', 100.5],
    ['Rancho Ejemplo 2', 'San Luis Potosí', 50.25],
  ],
};

const LOTES_SHEET: SheetSpec = {
  name: 'Lotes',
  headers: ['ranchoNombre', 'nombre', 'hectareas', 'tiposCultivo'],
  data: [
    ['Rancho Ejemplo 1', 'Lote A1', 25.5, 'Tomate'],
    ['Rancho Ejemplo 1', 'Lote A2', 30.0, 'Chile'],
  ],
};

const USUARIOS_SHEET: SheetSpec = {
  name: 'Usuarios',
  headers: ['numeroTelefono', 'nombre', 'roles'],
  data: [
    ['+5216140000001', 'Juan Pérez', 'FIELD_WORKER'],
    ['+5216140000002', 'María García', 'SUPERVISOR'],
  ],
};

const CATALOGO_SHEET: SheetSpec = {
  name: 'Catalogo',
  headers: ['nombre', 'categoria', 'unidad', 'costoUnitario'],
  data: [
    ['Abamectin', 'Insecticida', 'L', 250.5],
    ['Fertilizante NPK', 'Fertilizante', 'kg', 15.75],
  ],
};

/**
 * Generates and downloads an Excel template for bulk import.
 * Templates include one sheet with sample data and headers.
 */
export function downloadRanchosTemplate() {
  return downloadWorkbook('template-ranchos.xlsx', [RANCHOS_SHEET]);
}

export function downloadLotesTemplate() {
  return downloadWorkbook('template-lotes.xlsx', [LOTES_SHEET]);
}

export function downloadUsuariosTemplate() {
  return downloadWorkbook('template-usuarios.xlsx', [USUARIOS_SHEET]);
}

export function downloadProductosTemplate() {
  return downloadWorkbook('template-productos.xlsx', [CATALOGO_SHEET]);
}

/**
 * Generates a comprehensive Excel template with all sheets.
 * Useful for downloading a single file with all import templates.
 */
export function downloadCompleteTemplate() {
  return downloadWorkbook('template-agrovid-completo.xlsx', [RANCHOS_SHEET, LOTES_SHEET, USUARIOS_SHEET, CATALOGO_SHEET]);
}

const DRONE_FLIGHTS_SHEET: SheetSpec = {
  name: 'Vuelos',
  headers: ['fecha', 'tipo', 'lote', 'zona', 'valvulas', 'hectareas', 'litros', 'notas'],
  data: [
    ['2026-10-05', 'aplicacion', 'Lote 4', '', 'V1, V2', 18.7, 560, 'Quema herbicida'],
    ['2026-10-06', 'monitoreo', 'Lote 4', '', '', '', '', 'Recorrido de revisión'],
  ],
};

const DRONE_FLIGHTS_HELP_SHEET: SheetSpec = {
  name: 'Instrucciones',
  headers: ['columna', 'obligatoria', 'descripcion'],
  data: [
    ['fecha', 'Sí', 'AAAA-MM-DD o DD/MM/AAAA (también una celda de fecha de Excel)'],
    ['tipo', 'No', 'aplicacion (por defecto), video o monitoreo'],
    ['lote', 'Sí', 'Nombre exacto del lote tal como aparece en Agrovid'],
    ['zona', 'No', 'Código o nombre de la zona dentro del lote'],
    ['valvulas', 'No', 'Códigos separados por coma, punto y coma o espacio (V1, V2)'],
    ['hectareas', 'Aplicación', 'Superficie tratada (≥ 0). Una aplicación requiere hectáreas o litros'],
    ['litros', 'Aplicación', 'Litros aplicados (≥ 0)'],
    ['notas', 'No', 'Texto libre (máx. 500 caracteres)'],
    ['', '', 'Máximo 1000 filas. Reimportar el mismo archivo no duplica vuelos. Las aplicaciones se registran confirmadas; video y monitoreo quedan en borrador hasta subir el video.'],
  ],
};

const EMPLOYEES_SHEET: SheetSpec = {
  name: 'Personal',
  headers: ['nombre', 'numero', 'cuadrilla'],
  data: [
    ['Juan Pérez López', '1001', 'Cuadrilla 1'],
    ['María García Ruiz', '1002', ''],
  ],
};

const EMPLOYEES_HELP_SHEET: SheetSpec = {
  name: 'Instrucciones',
  headers: ['columna', 'obligatoria', 'descripcion'],
  data: [
    ['nombre', 'Sí', 'Nombre y apellido'],
    ['numero', 'No', 'Número o clave de empleado'],
    ['cuadrilla', 'No', 'Nombre de una cuadrilla existente; si no existe se importa sin cuadrilla'],
    ['', '', 'Reimportar el mismo archivo no duplica personas.'],
  ],
};

/** Plantilla de importación de personal. */
export function downloadEmployeesTemplate() {
  return downloadWorkbook('template-personal.xlsx', [EMPLOYEES_SHEET, EMPLOYEES_HELP_SHEET]);
}

/** Plantilla de importación de vuelos de dron (hoja de ejemplo + hoja de instrucciones). */
export function downloadDroneFlightsTemplate() {
  return downloadWorkbook('template-vuelos-dron.xlsx', [DRONE_FLIGHTS_SHEET, DRONE_FLIGHTS_HELP_SHEET]);
}
